import deepEqual from "deep-equal";

import { definedMap } from "@glideapps/ts-necessities";

import MaterialGlideApp, { MaterialGlideAppState } from "../material-glide-app/material-glide-app";
import { logInfo } from "common/debug-print";
import {
    SerializedApp,
    firstNonHiddenNonFlyoutTabIndex,
    getAppFeatures,
    getSourceMetadata,
    SourceMetadata,
} from "common/components/SerializedApp";
import { glideAppStateFromProps } from "../../../../lib/glide-app-state-from-props";
import { createNavigationModel, AppNavigationModel } from "../../../lib/navigation-model";
import { loadedDefinedMap, asString } from "common/components/component-helpers";
import { defined, nullToUndefined, panic } from "common/Support";
import { PaymentInformationForBuyButtons } from "common/Database";
import { TableName } from "common/database-strings";
import { Evaluable, LOADING, EnvironmentBoundEvaluable, ComponentMoveAction } from "common/components/types";
import { TypeSchema, getMenuScreenName } from "common/generator/description";
import { trackPushScreen } from "../../../../analytics";
import { EminenceFlags, eminenceFlags, Eminence } from "common/Database/eminence";

import { MaterialViewerGlideAppProps, MaterialViewerGlideAppState } from "./material-viewer-glide-app-types";
import { QuotaValuesForKinds } from "common/Database/quotas";
import { DeviceFormFactor } from "common/render/form-factor";

const onAppNavigationChange = (appID: string, navModel: AppNavigationModel) => {
    const { currentScreenTitle } = navModel;
    trackPushScreen(appID, currentScreenTitle);
};

export default class MaterialViewerGlideApp extends MaterialGlideApp<
    MaterialViewerGlideAppProps,
    MaterialViewerGlideAppState
> {
    public static getDerivedStateFromProps(
        props: MaterialViewerGlideAppProps,
        oldState: MaterialViewerGlideAppState | null
    ): MaterialViewerGlideAppState | null {
        const { serializedApp, appEnvironment } = props;

        // FIXME: deepEqual is pretty expensive for larger serializedApps.
        // We expect frame skipping while this is running and the objects
        // aren't identical (identity is a fast-path case for deepEqual).
        // Is it possible to make this entire process async?
        // FIXME: Also note the comment in `GlideAppPlayer.tsx`.
        if (
            oldState !== null &&
            appEnvironment === oldState.appEnvironment &&
            deepEqual(oldState.serializedApp, serializedApp, { strict: true })
        ) {
            const r = {
                ...oldState,
                serializedApp,
            };
            return r;
        }

        logInfo("making state");

        const { state, evaluatedRoots } = glideAppStateFromProps(
            serializedApp,
            appEnvironment,
            (screens, functions) => ({ screens, functions })
        );

        // If a deserialization error occurs, force-reload the page.
        // This can happen when we introduce a new component, but the
        // user's cached Glide doesn't support it yet.
        if (evaluatedRoots === undefined) {
            document.location.reload(true);
            return null;
        }

        const { appID } = appEnvironment;
        const navigationModel = createNavigationModel(
            defined(evaluatedRoots).map(pivots => ({ ...pivots, provenance: undefined })),
            firstNonHiddenNonFlyoutTabIndex(serializedApp.tabs),
            { onAppNavigationPush: m => onAppNavigationChange(appID, m) }
        );
        trackPushScreen(appID, navigationModel.currentScreenTitle);

        const newState = {
            ...state,
            navigationModel,
            serializedApp,
            appEnvironment,
        };

        logInfo("done making state");

        return newState;
    }

    public get deviceFormFactor(): DeviceFormFactor {
        return this.props.deviceFormFactor;
    }

    public get isTrialExpired(): boolean {
        return getAppFeatures(this.serializedApp).isTrialLocked === true;
    }

    public get shortName(): string | undefined {
        return this.props.shortName;
    }

    public get customDomain(): string | undefined {
        return this.props.customDomain;
    }

    protected get serializedApp(): SerializedApp {
        return this.props.serializedApp;
    }

    public get paymentInformation(): PaymentInformationForBuyButtons {
        return this.props.paymentInfomation;
    }

    public get demonstratingProComponents(): boolean {
        // We don't demonstrate Pro components in the player. You have to be subscribed for them to work.
        return false;
    }

    public get eminenceFlags(): EminenceFlags {
        // This is for legacy apps which don't have an eminence yet.
        const legacyEminence = getAppFeatures(this.serializedApp).removeBranding ? Eminence.Pro : Eminence.Free;

        const eminence = this.props.eminence ?? legacyEminence;
        return eminenceFlags[eminence] ?? eminenceFlags[legacyEminence];
    }

    protected get fallbackAuthor(): string | undefined {
        return this.serializedApp.manifest?.author;
    }

    public get isPublished(): boolean {
        // We're in the player, so of course we're published.
        return true;
    }

    protected get glideAppState(): MaterialGlideAppState | undefined {
        return nullToUndefined(this.state);
    }

    protected updateGlideAppState(f: (current: MaterialGlideAppState) => MaterialGlideAppState) {
        this.setState(f);
    }

    public quotasDidUpdate(quotas: QuotaValuesForKinds, prior: QuotaValuesForKinds): void {
        // If the quotas change, the data store will need to re-read
        // from upstream to capture all of the rows it may have
        // already ignored.
        if (quotas["rows-used"]?.limit !== prior["rows-used"]?.limit) {
            this.props.appEnvironment.dataStore.resetFromUpstream();
        }
    }

    public getComputedColumn(_schema: TypeSchema, tableName: TableName, columnName: string): Evaluable | undefined {
        const state = this.glideAppState;
        if (state === undefined) return undefined;

        const tableEntry = state.computedColumns.get(tableName.name);
        if (tableEntry === undefined) return undefined;

        return tableEntry.get(columnName);
    }

    public getSourceMetadata(): readonly SourceMetadata[] | undefined {
        return getSourceMetadata(this.serializedApp);
    }

    public registerColumnFormatChangedCallback(): void {
        return panic("Column formats are only dynamic in the builder");
    }

    public componentMoved(_sourceComponentID: string, _targetComponentID: string, _action: ComponentMoveAction): void {
        return panic("Components can only be moved in the builder");
    }

    public unregisterColumnFormatChangedCallback(): void {
        return panic("Column formats are only dynamic in the builder");
    }

    public getColumnFormat(): EnvironmentBoundEvaluable | undefined {
        return panic("Column formats are only dynamic in the builder");
    }

    protected makeMenuScreen(menuID: string, primaryKeyHash: string, defaultScreenName: string): string {
        const screenName = getMenuScreenName(menuID, primaryKeyHash);
        if (this.serializedApp.screens[screenName] !== undefined) {
            return screenName;
        } else {
            return defaultScreenName;
        }
    }

    public addImageComponent(): void {
        return panic("Adding component is only supported in the builder");
    }

    public addComponentForColumn(): void {
        return panic("Adding component is only supported in the builder");
    }

    public startBuilderBusy(): void {
        return panic("Builder busy is only supported in the builder");
    }

    public stopBuilderBusy(): void {
        return panic("Builder busy is only supported in the builder");
    }

    public setCurrentTab(tIndex: number): void {
        if (this.currentTabs?.[tIndex]?.isVisible !== true) return;

        super.setCurrentTab(tIndex);
    }

    public render(): React.ReactNode {
        if (this.glideAppState !== undefined) {
            // This is all actually for Google Analytics.
            // Even in combined App + Web views, it really wants to work off of page titles.
            // So to make it happy we put the screen title in the page title too.
            let pageTitlePrefix = "";
            let pageTitlePostfix = "";
            const screen = this.glideAppState.navigationModel.currentScreen;
            if (this.props.serializedApp.features !== undefined && this.props.serializedApp.features.removeBranding) {
                pageTitlePrefix = `${this.props.serializedApp.title} · `;
            } else if (this.props.useFrame) {
                pageTitlePrefix = `${this.props.serializedApp.title} · `;
                pageTitlePostfix = "Made with Glide";
            }
            const currentScreenTitle = loadedDefinedMap(
                definedMap(screen?.title, t => t.current),
                asString
            );
            const pageTitlePunctuatedPostfix = pageTitlePostfix.length > 0 ? ` · ${pageTitlePostfix}` : "";
            if (currentScreenTitle !== undefined && currentScreenTitle !== LOADING) {
                document.title = pageTitlePrefix + currentScreenTitle + pageTitlePunctuatedPostfix;
            } else if (pageTitlePrefix.length > 0) {
                document.title = pageTitlePrefix + pageTitlePostfix;
            } else {
                document.title = "App" + pageTitlePunctuatedPostfix;
            }
        }
        return super.render();
    }
}
